import React, { useEffect, useRef, useState } from "react";
import {
    FaTools,
    FaGraduationCap,
    FaHeart,
    FaLightbulb,
    FaUsers,
    FaBookOpen,
    FaAward,
    FaStar,
    FaBullseye,
    FaEye,
    FaCut,
    FaCheckCircle,
    FaBolt,
    FaBriefcase,
    FaWrench,
    FaLaptopCode,
    FaUserTie,
    FaArrowRight,
    FaHammer,
    FaCertificate,
} from "react-icons/fa";

import bgImage from "./trading.jpg";

const About = () => {
    const [visibleSections, setVisibleSections] = useState([]);
    const sectionRefs = useRef([]);

    useEffect(() => {
        const observers = [];

        sectionRefs.current.forEach((section, index) => {
            if (!section) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setVisibleSections((previous) =>
                                previous.includes(index)
                                    ? previous
                                    : [...previous, index]
                            );

                            observer.unobserve(entry.target);
                        }
                    });
                },
                {
                    threshold: 0.12,
                }
            );

            observer.observe(section);
            observers.push(observer);
        });

        return () => {
            observers.forEach((observer) => observer.disconnect());
        };
    }, []);

    const registerSection = (element) => {
        if (element && !sectionRefs.current.includes(element)) {
            sectionRefs.current.push(element);
        }
    };

    return (
        <main className="trade-about-page">

            {/* =========================================================
                HERO
            ========================================================= */}
            <section
                className="trade-about-hero"
                style={{
                    backgroundImage: `url(${bgImage})`,
                }}
            >
                <div className="trade-hero-overlay" />

                <div className="trade-hero-pattern" />

                <div className="trade-hero-content">
                    <div className="trade-hero-icon">
                        <FaTools />
                    </div>

                    <div className="trade-hero-label">
                        <span />
                        VOCATIONAL & TRADE TRAINING
                        <span />
                    </div>

                    <h1>
                        Learn Skills.
                        <br />
                        <span>Build Your Future.</span>
                    </h1>

                    <p>
                        Empowering students with practical technical skills,
                        professional knowledge, creativity, confidence, and
                        entrepreneurial abilities for the world of work.
                    </p>

                    <div className="trade-hero-buttons">
                        <a
                            href="#our-story"
                            className="trade-primary-button"
                        >
                            Discover Our School
                            <FaArrowRight />
                        </a>

                        <a
                            href="#mission"
                            className="trade-secondary-button"
                        >
                            Our Mission
                        </a>
                    </div>

                    <div className="trade-hero-features">
                        <div>
                            <FaTools />
                            <span>Hands-On Training</span>
                        </div>

                        <div>
                            <FaCertificate />
                            <span>Career Skills</span>
                        </div>

                        <div>
                            <FaBriefcase />
                            <span>Work Readiness</span>
                        </div>
                    </div>
                </div>

                <div className="trade-scroll-indicator">
                    <span />
                    <small>SCROLL TO EXPLORE</small>
                </div>
            </section>

            {/* =========================================================
                INTRODUCTION
            ========================================================= */}
            <section
                id="our-story"
                className={`trade-introduction ${
                    visibleSections.includes(0)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-intro-container">

                    <div className="trade-intro-image">
                        <div className="trade-intro-image-frame">
                            <img
                                src={bgImage}
                                alt="Vocational and trade training"
                            />

                            <div className="trade-image-overlay" />

                            <div className="trade-image-corner top-left" />
                            <div className="trade-image-corner bottom-right" />

                            <div className="trade-image-badge">
                                <FaTools />

                                <div>
                                    <strong>Practical</strong>
                                    <span>Skills Training</span>
                                </div>
                            </div>

                            <div className="trade-image-number">
                                <span>01</span>
                            </div>
                        </div>
                    </div>

                    <div className="trade-intro-content">
                        <span className="trade-section-label">
                            WHO WE ARE
                        </span>

                        <h2>
                            Training People With
                            <span> Skills That Matter.</span>
                        </h2>

                        <div className="trade-divider">
                            <span />
                            <span />
                        </div>

                        <p>
                            Our vocational and trade school is committed to
                            providing practical, career-focused training that
                            equips learners with useful technical skills and
                            the confidence to apply those skills in real-world
                            situations.
                        </p>

                        <p>
                            We believe that education should not only provide
                            knowledge but should also give people the ability
                            to create, repair, build, design, serve, innovate,
                            and earn a living through productive work.
                        </p>

                        <p>
                            Through hands-on instruction, workshop practice,
                            demonstrations, projects, and professional
                            guidance, we help learners develop the technical
                            competence and discipline required for employment,
                            entrepreneurship, and continued professional
                            development.
                        </p>

                        <div className="trade-intro-highlights">
                            <div>
                                <FaCheckCircle />
                                <span>Hands-On Training</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Industry-Focused Skills</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Entrepreneurship</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Workplace Readiness</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================
                MISSION / VISION
            ========================================================= */}
            <section
                id="mission"
                className={`trade-purpose-section ${
                    visibleSections.includes(1)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-section-heading">
                    <span className="trade-section-label">
                        OUR PURPOSE
                    </span>

                    <h2>
                        Skills With
                        <span> Purpose.</span>
                    </h2>

                    <p>
                        Our training is designed to turn practical learning
                        into real opportunities for work, business, and
                        personal development.
                    </p>
                </div>

                <div className="trade-purpose-grid">

                    <div className="trade-purpose-card mission">
                        <div className="trade-purpose-top">
                            <div className="trade-purpose-icon">
                                <FaBullseye />
                            </div>

                            <span>01</span>
                        </div>

                        <div className="trade-purpose-label">
                            OUR MISSION
                        </div>

                        <h3>
                            Empowering Through Practical Skills
                        </h3>

                        <p>
                            To provide accessible, practical, and quality
                            vocational training that equips learners with
                            technical competence, creativity, discipline,
                            problem-solving abilities, and entrepreneurial
                            skills for productive careers.
                        </p>

                        <div className="trade-purpose-bottom">
                            <FaTools />
                            <span>Learn • Practice • Master</span>
                        </div>
                    </div>

                    <div className="trade-purpose-card vision">
                        <div className="trade-purpose-top">
                            <div className="trade-purpose-icon">
                                <FaEye />
                            </div>

                            <span>02</span>
                        </div>

                        <div className="trade-purpose-label">
                            OUR VISION
                        </div>

                        <h3>
                            Building Skilled & Productive People
                        </h3>

                        <p>
                            To become a respected center for vocational and
                            technical training, developing skilled,
                            innovative, responsible, and self-reliant
                            individuals who contribute positively to their
                            communities and the economy.
                        </p>

                        <div className="trade-purpose-bottom">
                            <FaBriefcase />
                            <span>Skills • Opportunity • Growth</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================
                TRAINING APPROACH
            ========================================================= */}
            <section
                className={`trade-approach-section ${
                    visibleSections.includes(2)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-approach-container">

                    <div className="trade-approach-content">
                        <span className="trade-section-label">
                            OUR TRAINING APPROACH
                        </span>

                        <h2>
                            Learn By
                            <span> Doing.</span>
                        </h2>

                        <div className="trade-divider">
                            <span />
                            <span />
                        </div>

                        <p>
                            Vocational education becomes meaningful when
                            learners can put knowledge into practice. Our
                            approach combines instruction with practical
                            exercises, demonstrations, workshop activities,
                            projects, and real-world problem solving.
                        </p>

                        <p>
                            Students are encouraged to ask questions,
                            experiment, solve problems, work with others, and
                            develop the discipline required to perform tasks
                            safely and professionally.
                        </p>

                        <div className="trade-process">

                            <div className="trade-process-item">
                                <div className="trade-process-number">
                                    01
                                </div>

                                <div>
                                    <h4>Learn</h4>
                                    <p>
                                        Understand the principles,
                                        techniques, tools, and safety
                                        procedures.
                                    </p>
                                </div>
                            </div>

                            <div className="trade-process-item">
                                <div className="trade-process-number">
                                    02
                                </div>

                                <div>
                                    <h4>Practice</h4>
                                    <p>
                                        Apply knowledge through practical
                                        exercises, projects, and workshop
                                        activities.
                                    </p>
                                </div>
                            </div>

                            <div className="trade-process-item">
                                <div className="trade-process-number">
                                    03
                                </div>

                                <div>
                                    <h4>Master</h4>
                                    <p>
                                        Develop confidence and competence to
                                        perform tasks independently and
                                        professionally.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="trade-approach-visual">

                        <div className="trade-visual-main">
                            <div className="trade-visual-icon">
                                <FaWrench />
                            </div>

                            <span>VOCATIONAL EDUCATION</span>

                            <h3>
                                Skills You Can
                                <strong> Use.</strong>
                            </h3>

                            <p>
                                Practical knowledge designed to prepare
                                learners for real work and real opportunity.
                            </p>

                            <div className="trade-visual-tools">
                                <span>
                                    <FaBolt />
                                </span>

                                <span>
                                    <FaHammer />
                                </span>

                                <span>
                                    <FaLaptopCode />
                                </span>

                                <span>
                                    <FaCut />
                                </span>
                            </div>
                        </div>

                        <div className="trade-visual-small-card">
                            <FaCertificate />

                            <div>
                                <strong>Practical Training</strong>
                                <span>
                                    Skills for the real world
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================
                CORE VALUES
            ========================================================= */}
            <section
                className={`trade-values-section ${
                    visibleSections.includes(3)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-section-heading">
                    <span className="trade-section-label">
                        WHAT WE BELIEVE
                    </span>

                    <h2>
                        Our Core
                        <span> Values.</span>
                    </h2>

                    <p>
                        These principles guide the way we train, work,
                        communicate, and prepare learners for professional
                        life.
                    </p>
                </div>

                <div className="trade-values-grid">

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            01
                        </div>

                        <div className="trade-value-icon">
                            <FaBookOpen />
                        </div>

                        <h3>Excellence</h3>

                        <p>
                            We encourage high standards in practical work,
                            technical knowledge, professionalism, and
                            continuous improvement.
                        </p>
                    </div>

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            02
                        </div>

                        <div className="trade-value-icon">
                            <FaHeart />
                        </div>

                        <h3>Integrity</h3>

                        <p>
                            We promote honesty, responsibility, respect,
                            accountability, and ethical behavior in every
                            aspect of training and work.
                        </p>
                    </div>

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            03
                        </div>

                        <div className="trade-value-icon">
                            <FaLightbulb />
                        </div>

                        <h3>Innovation</h3>

                        <p>
                            We encourage learners to think creatively,
                            identify problems, develop solutions, and embrace
                            new ideas and technologies.
                        </p>
                    </div>

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            04
                        </div>

                        <div className="trade-value-icon">
                            <FaUsers />
                        </div>

                        <h3>Teamwork</h3>

                        <p>
                            We believe collaboration, communication, and
                            mutual respect are essential skills in every
                            workplace and business environment.
                        </p>
                    </div>

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            05
                        </div>

                        <div className="trade-value-icon">
                            <FaAward />
                        </div>

                        <h3>Professionalism</h3>

                        <p>
                            We prepare learners to demonstrate punctuality,
                            discipline, quality workmanship, responsibility,
                            and respect for clients and colleagues.
                        </p>
                    </div>

                    <div className="trade-value-card">
                        <div className="trade-value-number">
                            06
                        </div>

                        <div className="trade-value-icon">
                            <FaStar />
                        </div>

                        <h3>Potential</h3>

                        <p>
                            We recognize that every learner can develop
                            valuable abilities when given the right training,
                            guidance, practice, and opportunity.
                        </p>
                    </div>
                </div>
            </section>

            {/* =========================================================
                WHAT STUDENTS GAIN
            ========================================================= */}
            <section
                className={`trade-benefits-section ${
                    visibleSections.includes(4)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-benefits-container">

                    <div className="trade-benefits-header">
                        <span className="trade-section-label">
                            WHAT STUDENTS GAIN
                        </span>

                        <h2>
                            More Than
                            <span> A Certificate.</span>
                        </h2>

                        <p>
                            Our goal is to develop capable people who can use
                            what they have learned with confidence,
                            responsibility, and purpose.
                        </p>
                    </div>

                    <div className="trade-benefits-grid">

                        <div className="trade-benefit">
                            <div className="trade-benefit-icon">
                                <FaTools />
                            </div>

                            <div>
                                <h3>Technical Skills</h3>
                                <p>
                                    Practical knowledge and hands-on
                                    competence within a chosen trade.
                                </p>
                            </div>
                        </div>

                        <div className="trade-benefit">
                            <div className="trade-benefit-icon">
                                <FaBriefcase />
                            </div>

                            <div>
                                <h3>Work Readiness</h3>
                                <p>
                                    Professional habits and skills needed to
                                    operate effectively in the workplace.
                                </p>
                            </div>
                        </div>

                        <div className="trade-benefit">
                            <div className="trade-benefit-icon">
                                <FaLightbulb />
                            </div>

                            <div>
                                <h3>Problem Solving</h3>
                                <p>
                                    The ability to analyze practical problems
                                    and develop useful solutions.
                                </p>
                            </div>
                        </div>

                        <div className="trade-benefit">
                            <div className="trade-benefit-icon">
                                <FaUserTie />
                            </div>

                            <div>
                                <h3>Entrepreneurship</h3>
                                <p>
                                    Skills and confidence to explore
                                    self-employment and business opportunities.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* =========================================================
                STATS / HIGHLIGHT BAR
            ========================================================= */}
            <section className="trade-highlight-section">
                <div className="trade-highlight-overlay" />

                <div className="trade-highlight-container">

                    <div className="trade-highlight-item">
                        <FaTools />

                        <strong>Practical</strong>

                        <span>Training</span>
                    </div>

                    <div className="trade-highlight-item">
                        <FaGraduationCap />

                        <strong>Technical</strong>

                        <span>Knowledge</span>
                    </div>

                    <div className="trade-highlight-item">
                        <FaCertificate />

                        <strong>Professional</strong>

                        <span>Development</span>
                    </div>

                    <div className="trade-highlight-item">
                        <FaBriefcase />

                        <strong>Career</strong>

                        <span>Opportunity</span>
                    </div>
                </div>
            </section>

            {/* =========================================================
                CTA
            ========================================================= */}
            <section
                className={`trade-about-cta ${
                    visibleSections.includes(5)
                        ? "trade-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="trade-cta-decoration decoration-one" />
                <div className="trade-cta-decoration decoration-two" />

                <div className="trade-cta-content">

                    <span className="trade-section-label">
                        START YOUR JOURNEY
                    </span>

                    <h2>
                        Learn a Skill.
                        <br />
                        <span>Build Your Future.</span>
                    </h2>

                    <p>
                        Discover practical training opportunities designed to
                        help you develop valuable skills, grow professionally,
                        and prepare for meaningful work or entrepreneurship.
                    </p>

                    <div className="trade-cta-buttons">
                        <a
                            href="#courses"
                            className="trade-cta-primary"
                        >
                            Explore Our Courses
                            <FaArrowRight />
                        </a>

                        <a
                            href="#contact"
                            className="trade-cta-secondary"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* =========================================================
                PAGE STYLES
            ========================================================= */}
            <style>{`
                * {
                    box-sizing: border-box;
                }

                .trade-about-page {
                    width: 100%;
                    overflow: hidden;

                    background: #ffffff;

                    color: #111111;

                    font-family:
                        "Poppins",
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }

                /* =====================================================
                   GLOBAL
                ===================================================== */

                .trade-section-label {
                    display: inline-flex;

                    align-items: center;

                    color: #00843d;

                    font-size: 11px;

                    font-weight: 900;

                    letter-spacing: 2.4px;
                }

                .trade-divider {
                    display: flex;

                    align-items: center;

                    gap: 6px;

                    margin:
                        18px 0 24px;
                }

                .trade-divider span {
                    display: block;

                    height: 4px;

                    border-radius: 20px;
                }

                .trade-divider span:first-child {
                    width: 45px;

                    background: #00843d;
                }

                .trade-divider span:last-child {
                    width: 20px;

                    background: #f4b400;
                }

                .trade-section-heading {
                    max-width: 780px;

                    margin:
                        0 auto 60px;

                    text-align: center;
                }

                .trade-section-heading h2 {
                    margin:
                        12px 0 15px;

                    color: #111111;

                    font-size:
                        clamp(
                            34px,
                            4vw,
                            52px
                        );

                    line-height: 1.08;

                    font-weight: 850;

                    letter-spacing: -1.5px;
                }

                .trade-section-heading h2 span {
                    color: #00843d;
                }

                .trade-section-heading p {
                    margin: 0;

                    color: #666666;

                    font-size: 14px;

                    line-height: 1.8;
                }

                /* =====================================================
                   HERO
                ===================================================== */

                .trade-about-hero {
                    position: relative;

                    width: 100%;
                    min-height: 760px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    padding:
                        110px 25px 100px;

                    background-size: cover;

                    background-position: center;

                    isolation: isolate;
                }

                .trade-hero-overlay {
                    position: absolute;

                    inset: 0;

                    z-index: -2;

                    background:
                        linear-gradient(
                            90deg,
                            rgba(
                                0,
                                0,
                                0,
                                0.9
                            ) 0%,
                            rgba(
                                0,
                                0,
                                0,
                                0.76
                            ) 48%,
                            rgba(
                                0,
                                0,
                                0,
                                0.45
                            ) 100%
                        );
                }

                .trade-hero-pattern {
                    position: absolute;

                    inset: 0;

                    z-index: -1;

                    opacity: 0.12;

                    background-image:
                        linear-gradient(
                            rgba(
                                255,
                                255,
                                255,
                                0.15
                            ) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(
                                255,
                                255,
                                255,
                                0.15
                            ) 1px,
                            transparent 1px
                        );

                    background-size:
                        55px 55px;
                }

                .trade-hero-content {
                    width: 100%;

                    max-width: 1150px;

                    margin: 0 auto;

                    color: #ffffff;
                }

                .trade-hero-icon {
                    width: 68px;
                    height: 68px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 25px;

                    border-radius: 18px;

                    background: #f4b400;

                    color: #111111;

                    font-size: 28px;

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.25
                        );
                }

                .trade-hero-label {
                    display: flex;

                    align-items: center;

                    gap: 12px;

                    margin-bottom: 18px;

                    color: #f4b400;

                    font-size: 11px;

                    font-weight: 850;

                    letter-spacing: 2.8px;
                }

                .trade-hero-label span {
                    width: 28px;
                    height: 2px;

                    background: #f4b400;
                }

                .trade-hero-content h1 {
                    max-width: 850px;

                    margin: 0;

                    font-size:
                        clamp(
                            45px,
                            6vw,
                            78px
                        );

                    line-height: 0.99;

                    font-weight: 900;

                    letter-spacing:
                        -3px;
                }

                .trade-hero-content h1 span {
                    color: #f4b400;
                }

                .trade-hero-content > p {
                    max-width: 720px;

                    margin:
                        27px 0 0;

                    color: #dddddd;

                    font-size: 15px;

                    line-height: 1.85;
                }

                .trade-hero-buttons {
                    display: flex;

                    flex-wrap: wrap;

                    gap: 13px;

                    margin-top: 32px;
                }

                .trade-primary-button,
                .trade-secondary-button {
                    min-height: 52px;

                    display: inline-flex;

                    align-items: center;
                    justify-content: center;

                    gap: 10px;

                    padding:
                        0 22px;

                    border-radius: 7px;

                    text-decoration: none;

                    font-size: 12px;

                    font-weight: 800;

                    transition:
                        transform 0.3s ease,
                        background 0.3s ease,
                        color 0.3s ease;
                }

                .trade-primary-button {
                    background: #f4b400;

                    color: #111111;
                }

                .trade-primary-button:hover {
                    transform:
                        translateY(-3px);

                    background: #ffffff;
                }

                .trade-secondary-button {
                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.5
                        );

                    color: #ffffff;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.07
                        );
                }

                .trade-secondary-button:hover {
                    transform:
                        translateY(-3px);

                    background: #00843d;

                    border-color:
                        #00843d;
                }

                .trade-hero-features {
                    display: flex;

                    flex-wrap: wrap;

                    gap: 25px;

                    margin-top: 45px;

                    padding-top: 25px;

                    border-top:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.2
                        );
                }

                .trade-hero-features div {
                    display: flex;

                    align-items: center;

                    gap: 9px;

                    color: #ffffff;

                    font-size: 11px;

                    font-weight: 650;
                }

                .trade-hero-features svg {
                    color: #f4b400;

                    font-size: 17px;
                }

                .trade-scroll-indicator {
                    position: absolute;

                    left: 50%;

                    bottom: 28px;

                    transform:
                        translateX(-50%);

                    display: flex;

                    flex-direction: column;

                    align-items: center;

                    gap: 8px;

                    color: #ffffff;

                    opacity: 0.75;
                }

                .trade-scroll-indicator span {
                    width: 1px;
                    height: 30px;

                    background: #f4b400;
                }

                .trade-scroll-indicator small {
                    font-size: 7px;

                    letter-spacing: 2px;
                }

                /* =====================================================
                   INTRODUCTION
                ===================================================== */

                .trade-introduction {
                    position: relative;

                    padding:
                        110px 25px;

                    background: #ffffff;
                }

                .trade-intro-container {
                    width: 100%;

                    max-width: 1200px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        minmax(0, 0.92fr)
                        minmax(0, 1.08fr);

                    gap: 80px;

                    align-items: center;
                }

                .trade-intro-image {
                    opacity: 0;

                    transform:
                        translateX(-50px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .trade-section-visible
                .trade-intro-image {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .trade-intro-image-frame {
                    position: relative;

                    min-height: 540px;

                    padding: 14px;

                    background: #111111;

                    border-radius: 20px;
                }

                .trade-intro-image-frame img {
                    width: 100%;
                    height: 100%;

                    min-height: 510px;

                    display: block;

                    object-fit: cover;

                    border-radius: 13px;
                }

                .trade-image-overlay {
                    position: absolute;

                    inset: 14px;

                    border-radius: 13px;

                    background:
                        linear-gradient(
                            to top,
                            rgba(
                                0,
                                0,
                                0,
                                0.58
                            ),
                            transparent 55%
                        );
                }

                .trade-image-corner {
                    position: absolute;

                    width: 65px;
                    height: 65px;

                    border-color:
                        #f4b400;

                    border-style: solid;

                    z-index: 5;
                }

                .trade-image-corner.top-left {
                    top: -8px;
                    left: -8px;

                    border-width:
                        4px 0 0 4px;

                    border-radius:
                        14px 0 0 0;
                }

                .trade-image-corner.bottom-right {
                    right: -8px;
                    bottom: -8px;

                    border-width:
                        0 4px 4px 0;

                    border-radius:
                        0 0 14px 0;
                }

                .trade-image-badge {
                    position: absolute;

                    left: 35px;
                    bottom: 35px;

                    z-index: 8;

                    display: flex;

                    align-items: center;

                    gap: 13px;

                    padding:
                        14px 17px;

                    border-radius: 12px;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.96
                        );

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.2
                        );
                }

                .trade-image-badge > svg {
                    color: #00843d;

                    font-size: 25px;
                }

                .trade-image-badge div {
                    display: flex;

                    flex-direction: column;
                }

                .trade-image-badge strong {
                    color: #111111;

                    font-size: 13px;
                }

                .trade-image-badge span {
                    color: #777777;

                    font-size: 10px;
                }

                .trade-image-number {
                    position: absolute;

                    top: 35px;
                    right: 35px;

                    z-index: 8;

                    width: 48px;
                    height: 48px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 50%;

                    background: #00843d;

                    color: #ffffff;

                    font-size: 12px;

                    font-weight: 900;

                    box-shadow:
                        0 8px 20px
                        rgba(
                            0,
                            0,
                            0,
                            0.25
                        );
                }

                .trade-intro-content {
                    opacity: 0;

                    transform:
                        translateX(50px);

                    transition:
                        opacity 0.8s ease 0.15s,
                        transform 0.8s ease 0.15s;
                }

                .trade-section-visible
                .trade-intro-content {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .trade-intro-content h2 {
                    max-width: 600px;

                    margin:
                        12px 0 0;

                    color: #111111;

                    font-size:
                        clamp(
                            34px,
                            4vw,
                            52px
                        );

                    line-height: 1.08;

                    font-weight: 850;

                    letter-spacing: -1.5px;
                }

                .trade-intro-content h2 span {
                    display: block;

                    color: #00843d;
                }

                .trade-intro-content p {
                    margin:
                        0 0 17px;

                    color: #666666;

                    font-size: 14px;

                    line-height: 1.85;
                }

                .trade-intro-highlights {
                    display: grid;

                    grid-template-columns:
                        1fr 1fr;

                    gap: 13px;

                    margin-top: 27px;
                }

                .trade-intro-highlights div {
                    display: flex;

                    align-items: center;

                    gap: 9px;

                    padding:
                        12px 13px;

                    border-radius: 8px;

                    background: #f6faf7;

                    color: #333333;

                    font-size: 11px;

                    font-weight: 700;
                }

                .trade-intro-highlights svg {
                    color: #00843d;

                    flex-shrink: 0;
                }

                /* =====================================================
                   PURPOSE
                ===================================================== */

                .trade-purpose-section {
                    padding:
                        110px 25px;

                    background:
                        #f4f8f5;
                }

                .trade-purpose-grid {
                    width: 100%;

                    max-width: 1050px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0, 1fr)
                        );

                    gap: 28px;
                }

                .trade-purpose-card {
                    position: relative;

                    padding:
                        35px;

                    min-height: 390px;

                    border-radius: 20px;

                    overflow: hidden;

                    transition:
                        transform 0.35s ease,
                        box-shadow 0.35s ease;
                }

                .trade-purpose-card:hover {
                    transform:
                        translateY(-8px);

                    box-shadow:
                        0 25px 50px
                        rgba(
                            0,
                            0,
                            0,
                            0.12
                        );
                }

                .trade-purpose-card.mission {
                    background: #00843d;

                    color: #ffffff;
                }

                .trade-purpose-card.vision {
                    background: #111111;

                    color: #ffffff;
                }

                .trade-purpose-card::after {
                    content: "";

                    position: absolute;

                    width: 220px;
                    height: 220px;

                    right: -100px;
                    bottom: -100px;

                    border-radius: 50%;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.07
                        );
                }

                .trade-purpose-top {
                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    margin-bottom: 35px;
                }

                .trade-purpose-icon {
                    width: 62px;
                    height: 62px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 17px;

                    background: #f4b400;

                    color: #111111;

                    font-size: 24px;
                }

                .trade-purpose-top > span {
                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.25
                        );

                    font-size: 45px;

                    line-height: 1;

                    font-weight: 900;
                }

                .trade-purpose-label {
                    margin-bottom: 10px;

                    color: #f4b400;

                    font-size: 10px;

                    font-weight: 900;

                    letter-spacing: 2px;
                }

                .trade-purpose-card h3 {
                    max-width: 400px;

                    margin:
                        0 0 16px;

                    font-size: 28px;

                    line-height: 1.2;

                    font-weight: 850;
                }

                .trade-purpose-card p {
                    max-width: 500px;

                    margin: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.72
                        );

                    font-size: 13px;

                    line-height: 1.8;
                }

                .trade-purpose-bottom {
                    position: absolute;

                    left: 35px;
                    right: 35px;
                    bottom: 28px;

                    display: flex;

                    align-items: center;

                    gap: 8px;

                    padding-top: 17px;

                    border-top:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.16
                        );

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.8
                        );

                    font-size: 10px;

                    font-weight: 750;

                    letter-spacing: 0.5px;
                }

                .trade-purpose-bottom svg {
                    color: #f4b400;
                }

                /* =====================================================
                   APPROACH
                ===================================================== */

                .trade-approach-section {
                    padding:
                        110px 25px;

                    background: #ffffff;
                }

                .trade-approach-container {
                    width: 100%;

                    max-width: 1200px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        minmax(0, 1fr)
                        minmax(0, 0.8fr);

                    gap: 85px;

                    align-items: center;
                }

                .trade-approach-content {
                    opacity: 0;

                    transform:
                        translateX(-50px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .trade-section-visible
                .trade-approach-content {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .trade-approach-content h2 {
                    margin:
                        12px 0 0;

                    color: #111111;

                    font-size:
                        clamp(
                            36px,
                            4vw,
                            55px
                        );

                    line-height: 1.05;

                    font-weight: 900;

                    letter-spacing: -2px;
                }

                .trade-approach-content h2 span {
                    color: #00843d;
                }

                .trade-approach-content > p {
                    margin:
                        0 0 16px;

                    color: #666666;

                    font-size: 14px;

                    line-height: 1.85;
                }

                .trade-process {
                    margin-top: 32px;
                }

                .trade-process-item {
                    display: grid;

                    grid-template-columns:
                        50px 1fr;

                    gap: 16px;

                    padding:
                        0 0 23px;

                    margin-bottom: 23px;

                    border-bottom:
                        1px solid
                        #eeeeee;
                }

                .trade-process-number {
                    width: 44px;
                    height: 44px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 50%;

                    background: #00843d;

                    color: #ffffff;

                    font-size: 11px;

                    font-weight: 900;
                }

                .trade-process-item:nth-child(2)
                .trade-process-number {
                    background: #f4b400;

                    color: #111111;
                }

                .trade-process-item h4 {
                    margin:
                        0 0 5px;

                    color: #111111;

                    font-size: 16px;

                    font-weight: 850;
                }

                .trade-process-item p {
                    margin: 0;

                    color: #777777;

                    font-size: 12px;

                    line-height: 1.65;
                }

                .trade-approach-visual {
                    position: relative;

                    min-height: 520px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    opacity: 0;

                    transform:
                        translateX(50px);

                    transition:
                        opacity 0.8s ease 0.15s,
                        transform 0.8s ease 0.15s;
                }

                .trade-section-visible
                .trade-approach-visual {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .trade-visual-main {
                    position: relative;

                    width: 390px;

                    min-height: 410px;

                    display: flex;

                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    padding: 45px;

                    border-radius: 28px;

                    background:
                        linear-gradient(
                            145deg,
                            #00843d,
                            #005f2c
                        );

                    color: #ffffff;

                    text-align: center;

                    box-shadow:
                        0 25px 60px
                        rgba(
                            0,
                            132,
                            61,
                            0.22
                        );
                }

                .trade-visual-main::before {
                    content: "";

                    position: absolute;

                    inset: 13px;

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.15
                        );

                    border-radius: 21px;
                }

                .trade-visual-icon {
                    width: 78px;
                    height: 78px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 25px;

                    border-radius: 22px;

                    background: #f4b400;

                    color: #111111;

                    font-size: 32px;
                }

                .trade-visual-main > span {
                    color: #f4b400;

                    font-size: 9px;

                    font-weight: 900;

                    letter-spacing: 2px;
                }

                .trade-visual-main h3 {
                    margin:
                        13px 0;

                    font-size: 34px;

                    line-height: 1.05;

                    font-weight: 850;
                }

                .trade-visual-main h3 strong {
                    display: block;

                    color: #f4b400;
                }

                .trade-visual-main p {
                    max-width: 270px;

                    margin: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.72
                        );

                    font-size: 12px;

                    line-height: 1.7;
                }

                .trade-visual-tools {
                    display: flex;

                    gap: 8px;

                    margin-top: 27px;
                }

                .trade-visual-tools span {
                    width: 36px;
                    height: 36px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 10px;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.1
                        );

                    color: #ffffff;
                }

                .trade-visual-small-card {
                    position: absolute;

                    right: 0;
                    bottom: 35px;

                    display: flex;

                    align-items: center;

                    gap: 11px;

                    padding:
                        14px 17px;

                    border-radius: 12px;

                    background: #ffffff;

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.16
                        );
                }

                .trade-visual-small-card > svg {
                    color: #00843d;

                    font-size: 22px;
                }

                .trade-visual-small-card div {
                    display: flex;

                    flex-direction: column;
                }

                .trade-visual-small-card strong {
                    color: #111111;

                    font-size: 11px;
                }

                .trade-visual-small-card span {
                    color: #777777;

                    font-size: 9px;
                }

                /* =====================================================
                   VALUES
                ===================================================== */

                .trade-values-section {
                    padding:
                        110px 25px;

                    background:
                        #f5f8f6;
                }

                .trade-values-grid {
                    width: 100%;

                    max-width: 1150px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            3,
                            minmax(0, 1fr)
                        );

                    gap: 20px;
                }

                .trade-value-card {
                    position: relative;

                    min-height: 275px;

                    padding: 30px;

                    border:
                        1px solid
                        #e8eee9;

                    border-radius: 17px;

                    background: #ffffff;

                    overflow: hidden;

                    transition:
                        transform 0.35s ease,
                        box-shadow 0.35s ease,
                        border-color 0.35s ease;
                }

                .trade-value-card:hover {
                    transform:
                        translateY(-8px);

                    border-color:
                        rgba(
                            0,
                            132,
                            61,
                            0.25
                        );

                    box-shadow:
                        0 20px 40px
                        rgba(
                            0,
                            0,
                            0,
                            0.08
                        );
                }

                .trade-value-number {
                    position: absolute;

                    top: 18px;
                    right: 22px;

                    color:
                        rgba(
                            0,
                            132,
                            61,
                            0.08
                        );

                    font-size: 42px;

                    font-weight: 900;
                }

                .trade-value-icon {
                    width: 53px;
                    height: 53px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 22px;

                    border-radius: 14px;

                    background:
                        rgba(
                            0,
                            132,
                            61,
                            0.09
                        );

                    color: #00843d;

                    font-size: 21px;

                    transition:
                        background 0.3s ease,
                        color 0.3s ease;
                }

                .trade-value-card:hover
                .trade-value-icon {
                    background: #00843d;

                    color: #f4b400;
                }

                .trade-value-card h3 {
                    margin:
                        0 0 10px;

                    color: #111111;

                    font-size: 19px;

                    font-weight: 850;
                }

                .trade-value-card p {
                    margin: 0;

                    color: #707070;

                    font-size: 12px;

                    line-height: 1.75;
                }

                /* =====================================================
                   BENEFITS
                ===================================================== */

                .trade-benefits-section {
                    padding:
                        105px 25px;

                    background: #ffffff;
                }

                .trade-benefits-container {
                    width: 100%;

                    max-width: 1150px;

                    margin: 0 auto;
                }

                .trade-benefits-header {
                    max-width: 680px;

                    margin-bottom: 50px;
                }

                .trade-benefits-header h2 {
                    margin:
                        12px 0 15px;

                    color: #111111;

                    font-size:
                        clamp(
                            35px,
                            4vw,
                            52px
                        );

                    line-height: 1.05;

                    font-weight: 900;

                    letter-spacing: -1.5px;
                }

                .trade-benefits-header h2 span {
                    color: #00843d;
                }

                .trade-benefits-header p {
                    margin: 0;

                    color: #6d6d6d;

                    font-size: 14px;

                    line-height: 1.8;
                }

                .trade-benefits-grid {
                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0, 1fr)
                        );

                    gap: 18px;
                }

                .trade-benefit {
                    display: flex;

                    align-items: flex-start;

                    gap: 17px;

                    padding: 25px;

                    border-radius: 15px;

                    background: #f7faf8;

                    border:
                        1px solid
                        #e8eee9;

                    transition:
                        transform 0.3s ease,
                        background 0.3s ease;
                }

                .trade-benefit:hover {
                    transform:
                        translateY(-5px);

                    background: #ffffff;
                }

                .trade-benefit-icon {
                    width: 50px;
                    height: 50px;

                    flex-shrink: 0;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 13px;

                    background: #00843d;

                    color: #f4b400;

                    font-size: 20px;
                }

                .trade-benefit h3 {
                    margin:
                        0 0 6px;

                    color: #111111;

                    font-size: 16px;

                    font-weight: 850;
                }

                .trade-benefit p {
                    margin: 0;

                    color: #707070;

                    font-size: 11.5px;

                    line-height: 1.7;
                }

                /* =====================================================
                   HIGHLIGHT
                ===================================================== */

                .trade-highlight-section {
                    position: relative;

                    padding:
                        65px 25px;

                    background:
                        #111111;

                    overflow: hidden;
                }

                .trade-highlight-overlay {
                    position: absolute;

                    inset: 0;

                    background:
                        radial-gradient(
                            circle at 20% 50%,
                            rgba(
                                0,
                                132,
                                61,
                                0.28
                            ),
                            transparent 30%
                        ),
                        radial-gradient(
                            circle at 85% 50%,
                            rgba(
                                244,
                                180,
                                0,
                                0.14
                            ),
                            transparent 25%
                        );
                }

                .trade-highlight-container {
                    position: relative;

                    z-index: 2;

                    width: 100%;

                    max-width: 1050px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );
                }

                .trade-highlight-item {
                    display: flex;

                    flex-direction: column;

                    align-items: center;

                    justify-content: center;

                    padding:
                        10px 20px;

                    text-align: center;

                    border-right:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.12
                        );
                }

                .trade-highlight-item:last-child {
                    border-right: 0;
                }

                .trade-highlight-item svg {
                    margin-bottom: 10px;

                    color: #f4b400;

                    font-size: 25px;
                }

                .trade-highlight-item strong {
                    color: #ffffff;

                    font-size: 15px;

                    font-weight: 850;
                }

                .trade-highlight-item span {
                    margin-top: 3px;

                    color: #999999;

                    font-size: 10px;
                }

                /* =====================================================
                   CTA
                ===================================================== */

                .trade-about-cta {
                    position: relative;

                    padding:
                        110px 25px;

                    background:
                        linear-gradient(
                            135deg,
                            #00843d 0%,
                            #00652f 60%,
                            #004e25 100%
                        );

                    overflow: hidden;

                    text-align: center;
                }

                .trade-cta-decoration {
                    position: absolute;

                    border-radius: 50%;

                    pointer-events: none;
                }

                .decoration-one {
                    width: 430px;
                    height: 430px;

                    top: -300px;
                    right: -120px;

                    border:
                        80px solid
                        rgba(
                            244,
                            180,
                            0,
                            0.09
                        );
                }

                .decoration-two {
                    width: 300px;
                    height: 300px;

                    bottom: -210px;
                    left: -120px;

                    background:
                        rgba(
                            0,
                            0,
                            0,
                            0.08
                        );
                }

                .trade-cta-content {
                    position: relative;

                    z-index: 5;

                    max-width: 800px;

                    margin: 0 auto;

                    opacity: 0;

                    transform:
                        translateY(35px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .trade-section-visible
                .trade-cta-content {
                    opacity: 1;

                    transform:
                        translateY(0);
                }

                .trade-cta-content
                .trade-section-label {
                    color: #f4b400;
                }

                .trade-cta-content h2 {
                    margin:
                        14px 0 18px;

                    color: #ffffff;

                    font-size:
                        clamp(
                            40px,
                            5vw,
                            65px
                        );

                    line-height: 1;

                    font-weight: 900;

                    letter-spacing: -2px;
                }

                .trade-cta-content h2 span {
                    color: #f4b400;
                }

                .trade-cta-content > p {
                    max-width: 680px;

                    margin: 0 auto;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.72
                        );

                    font-size: 14px;

                    line-height: 1.8;
                }

                .trade-cta-buttons {
                    display: flex;

                    justify-content: center;

                    flex-wrap: wrap;

                    gap: 12px;

                    margin-top: 30px;
                }

                .trade-cta-primary,
                .trade-cta-secondary {
                    min-height: 52px;

                    display: inline-flex;

                    align-items: center;
                    justify-content: center;

                    gap: 10px;

                    padding:
                        0 23px;

                    border-radius: 7px;

                    text-decoration: none;

                    font-size: 11px;

                    font-weight: 850;

                    transition:
                        transform 0.3s ease,
                        background 0.3s ease,
                        color 0.3s ease;
                }

                .trade-cta-primary {
                    background: #f4b400;

                    color: #111111;
                }

                .trade-cta-primary:hover {
                    transform:
                        translateY(-3px);

                    background: #ffffff;
                }

                .trade-cta-secondary {
                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.4
                        );

                    color: #ffffff;
                }

                .trade-cta-secondary:hover {
                    transform:
                        translateY(-3px);

                    background: #111111;

                    border-color: #111111;
                }

                /* =====================================================
                   TABLET
                ===================================================== */

                @media (max-width: 1050px) {
                    .trade-intro-container {
                        gap: 45px;
                    }

                    .trade-approach-container {
                        gap: 45px;
                    }

                    .trade-values-grid {
                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0, 1fr)
                            );
                    }
                }

                /* =====================================================
                   MOBILE
                ===================================================== */

                @media (max-width: 760px) {
                    .trade-about-hero {
                        min-height: 720px;

                        padding:
                            100px 20px 80px;
                    }

                    .trade-hero-content h1 {
                        font-size: 47px;

                        letter-spacing: -2px;
                    }

                    .trade-hero-content > p {
                        font-size: 13px;
                    }

                    .trade-hero-features {
                        display: grid;

                        grid-template-columns:
                            1fr 1fr;

                        gap: 13px;
                    }

                    .trade-hero-features div:last-child {
                        grid-column:
                            span 2;
                    }

                    .trade-introduction,
                    .trade-purpose-section,
                    .trade-approach-section,
                    .trade-values-section,
                    .trade-benefits-section {
                        padding:
                            75px 18px;
                    }

                    .trade-intro-container,
                    .trade-approach-container {
                        grid-template-columns: 1fr;

                        gap: 50px;
                    }

                    .trade-intro-image-frame {
                        min-height: 400px;
                    }

                    .trade-intro-image-frame img {
                        min-height: 370px;
                    }

                    .trade-intro-highlights {
                        grid-template-columns: 1fr;
                    }

                    .trade-purpose-grid {
                        grid-template-columns: 1fr;
                    }

                    .trade-purpose-card {
                        min-height: 390px;

                        padding: 28px;
                    }

                    .trade-purpose-card h3 {
                        font-size: 25px;
                    }

                    .trade-approach-visual {
                        min-height: 430px;
                    }

                    .trade-visual-main {
                        width: 100%;

                        max-width: 390px;

                        min-height: 390px;
                    }

                    .trade-visual-small-card {
                        right: 0;

                        bottom: 10px;
                    }

                    .trade-values-grid {
                        grid-template-columns: 1fr;
                    }

                    .trade-value-card {
                        min-height: auto;
                    }

                    .trade-benefits-grid {
                        grid-template-columns: 1fr;
                    }

                    .trade-highlight-container {
                        grid-template-columns:
                            1fr 1fr;
                    }

                    .trade-highlight-item {
                        min-height: 110px;

                        border-right: 0;
                    }

                    .trade-highlight-item:nth-child(-n + 2) {
                        border-bottom:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                0.12
                            );
                    }

                    .trade-about-cta {
                        padding:
                            80px 18px;
                    }

                    .trade-cta-content h2 {
                        font-size: 43px;
                    }
                }

                /* =====================================================
                   SMALL MOBILE
                ===================================================== */

                @media (max-width: 450px) {
                    .trade-about-hero {
                        min-height: 690px;

                        padding-left: 16px;
                        padding-right: 16px;
                    }

                    .trade-hero-icon {
                        width: 58px;
                        height: 58px;

                        font-size: 23px;
                    }

                    .trade-hero-label {
                        font-size: 8px;

                        letter-spacing: 1.8px;
                    }

                    .trade-hero-content h1 {
                        font-size: 40px;
                    }

                    .trade-hero-buttons {
                        flex-direction: column;
                    }

                    .trade-primary-button,
                    .trade-secondary-button {
                        width: 100%;
                    }

                    .trade-hero-features {
                        grid-template-columns: 1fr;
                    }

                    .trade-hero-features div:last-child {
                        grid-column: auto;
                    }

                    .trade-intro-image-frame {
                        min-height: 340px;
                    }

                    .trade-intro-image-frame img {
                        min-height: 310px;
                    }

                    .trade-image-badge {
                        left: 25px;
                        bottom: 25px;
                    }

                    .trade-image-number {
                        top: 25px;
                        right: 25px;
                    }

                    .trade-section-heading h2,
                    .trade-intro-content h2 {
                        font-size: 35px;
                    }

                    .trade-purpose-bottom {
                        left: 28px;
                        right: 28px;
                    }

                    .trade-visual-main {
                        min-height: 350px;

                        padding: 30px 20px;
                    }

                    .trade-visual-main h3 {
                        font-size: 29px;
                    }

                    .trade-visual-small-card {
                        transform: scale(0.88);

                        transform-origin:
                            bottom right;
                    }

                    .trade-highlight-container {
                        grid-template-columns: 1fr;
                    }

                    .trade-highlight-item {
                        min-height: 90px;

                        border-bottom:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                0.12
                            );
                    }

                    .trade-highlight-item:last-child {
                        border-bottom: 0;
                    }

                    .trade-cta-content h2 {
                        font-size: 38px;
                    }

                    .trade-cta-buttons {
                        flex-direction: column;
                    }

                    .trade-cta-primary,
                    .trade-cta-secondary {
                        width: 100%;
                    }
                }
            `}</style>
        </main>
    );
};

export default About;